import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Megaphone } from 'lucide-react';
import { Listing } from '../../types';
import { useIsraelNow } from '../../hooks/useIsraelNow';
import { latestMessages, pickLang } from '../../utils/listingDisplay';
import CategoryIcon from './CategoryIcon';
import { listingHref } from './ListingCard';

/** "2 Oct" / "2 באוק׳", in Israel time. */
function shortDate(iso: string, isHebrew: boolean): string {
  return new Date(iso).toLocaleDateString(isHebrew ? 'he-IL' : 'en-GB', {
    timeZone: 'Asia/Jerusalem',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * Newest active messages from the listings on this page (plan 4.5, phase 5):
 * a row of small cards that scrolls sideways; tapping one opens the listing.
 * Shown only when there is at least one message.
 */
export default function LatestMessagesStrip({ listings }: { listings: Listing[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const now = useIsraelNow(); // re-checks expiry every minute
  const items = useMemo(
    () => latestMessages(listings, new Date().toISOString()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listings, now.time]
  );

  if (items.length === 0) return null;

  return (
    <section aria-labelledby="latest-messages-title" className="space-y-2">
      <h2 id="latest-messages-title" className="flex items-center gap-1.5 text-sm font-semibold text-ink">
        <Megaphone className="w-4 h-4 rtl:-scale-x-100" aria-hidden />
        {t('directory.latestMessages.title')}
      </h2>
      <ul className="-mx-4 px-4 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 [scrollbar-width:thin]">
        {items.map(({ listing, message }) => (
          <li key={`${listing.id}-${message.id}`} className="snap-start shrink-0 w-[260px] max-w-[80vw]">
            <Link
              to={listingHref(listing)}
              className="flex h-full flex-col gap-1.5 rounded-[16px] border border-weekday-100 bg-weekday-50 p-3 hover:border-weekday-500 transition-colors"
            >
              <span className="flex items-center gap-1.5 min-w-0 text-xs font-medium text-weekday-700">
                <CategoryIcon category={listing.category} className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{pickLang(listing.nameEn, listing.nameHe, isHebrew)}</span>
                <span className="ms-auto shrink-0 text-ash font-normal">{shortDate(message.startsAt, isHebrew)}</span>
              </span>
              <span className="text-sm text-ink line-clamp-3 whitespace-pre-line break-words">
                {pickLang(message.textEn, message.textHe, isHebrew)}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
